import { matchPath } from "react-router-dom";
import { SLIDE_IN_MENUS } from "../../screens/root/constants";

/**
 * Single source of route patterns for entity detail views. The `path` string
 * is consumed in two places:
 *   - Landing routes in screens/router.tsx — registered behind the
 *     `detail_landings_v1` flag.
 *   - The `?modal=…` URL parser used by the slide-in URL bridge.
 *
 * Keeping one canonical list guarantees that a path which opens a slide-in
 * (e.g. `?modal=c/batman`) resolves to the same shape as the corresponding
 * landing path (`/c/batman`) — the modal-param literally embeds the landing
 * path. List specific routes before less specific ones (matchPath returns the
 * first hit).
 */
export interface DetailRoute {
  path: string;
  menu: SLIDE_IN_MENUS;
}

export const DETAIL_ROUTES: DetailRoute[] = [
  {
    path: "c/:comicSlug/r/:releaseUuid",
    menu: SLIDE_IN_MENUS.releaseInfo,
  },
  { path: "c/:comicSlug", menu: SLIDE_IN_MENUS.comicDetails },
  { path: "a/:artistSlug", menu: SLIDE_IN_MENUS.publisher },
  { path: "u/:username/:subpage", menu: SLIDE_IN_MENUS.userProfile },
  { path: "u/:username", menu: SLIDE_IN_MENUS.userProfile },
  { path: "b/:bundleUuid", menu: SLIDE_IN_MENUS.bundleDetails },
];

export interface MatchedModalRoute {
  menu: SLIDE_IN_MENUS;
  params: Record<string, string | undefined>;
}

export function matchModalPath(
  modalPath: string | null | undefined,
): MatchedModalRoute | null {
  if (!modalPath) return null;
  const normalized = modalPath.startsWith("/") ? modalPath : "/" + modalPath;
  for (const route of DETAIL_ROUTES) {
    const match = matchPath({ path: route.path, end: true }, normalized);
    if (match) {
      return { menu: route.menu, params: match.params };
    }
  }
  return null;
}

/**
 * True when `pathname` is currently rendering one of the landing routes —
 * /c/{slug}, /c/{slug}/r/{uuid}, /a/{slug}, /u/{name}, /u/{name}/{subpage},
 * or /b/{uuid}. Used by `useSlideInMenu` so that cross-entity clicks
 * fired from inside a *Content component navigate landing-to-landing instead
 * of opening a modal over a landing.
 */
export function isOnDetailLandingPath(pathname: string): boolean {
  return matchModalPath(pathname) !== null;
}

export interface ModalIdentity {
  comicSlug?: string;
  artistSlug?: string;
  username?: string;
  userSubpage?: string;
  bundleUuid?: string;
  releaseUuid?: string;
}

/**
 * Build the `modal` query-param value for a given menu + identifier set.
 * Returns null when required fields are missing — caller should not push
 * a partial URL.
 */
export function buildModalPath(
  menu: SLIDE_IN_MENUS,
  identity: ModalIdentity,
): string | null {
  switch (menu) {
    case SLIDE_IN_MENUS.comicDetails:
      return identity.comicSlug
        ? `c/${encodeURIComponent(identity.comicSlug)}`
        : null;
    case SLIDE_IN_MENUS.releaseInfo:
      if (!identity.comicSlug || !identity.releaseUuid) {
        return null;
      }
      return `c/${encodeURIComponent(identity.comicSlug)}/r/${encodeURIComponent(identity.releaseUuid)}`;
    case SLIDE_IN_MENUS.publisher:
      return identity.artistSlug
        ? `a/${encodeURIComponent(identity.artistSlug)}`
        : null;
    case SLIDE_IN_MENUS.userProfile:
      if (!identity.username) return null;
      return identity.userSubpage
        ? `u/${encodeURIComponent(identity.username)}/${encodeURIComponent(identity.userSubpage)}`
        : `u/${encodeURIComponent(identity.username)}`;
    case SLIDE_IN_MENUS.bundleDetails:
      return identity.bundleUuid
        ? `b/${encodeURIComponent(identity.bundleUuid)}`
        : null;
    default:
      return null;
  }
}
